/**
 * DirectChatWindow — the actual message list + composer for one thread.
 *
 * Used by both the buyer inbox (`/messages`) and the seller inbox
 * (`/workspace/inbox`). Pure UI — parent owns the thread ID and
 * onReadRefresh callback.
 */
import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Package, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

interface Message {
  message_id: string;
  thread_id: string;
  sender_user_id: string;
  sender_role: "buyer" | "seller";
  sender_name?: string;
  body: string;
  created_at: string;
}

interface Thread {
  thread_id: string;
  buyer_user_id: string;
  seller_user_id: string;
  buyer_name?: string;
  seller_workspace_name?: string;
  product?: { title?: string; slug?: string; image_url?: string; brand?: string };
  closed?: boolean;
  last_message_preview?: string;
}

export const DirectChatWindow: React.FC<{
  threadId: string;
  onRead?: () => void;
}> = ({ threadId, onRead }) => {
  const { user } = useAuth();
  const [thread, setThread] = React.useState<Thread | null>(null);
  const [viewerRole, setViewerRole] = React.useState<"buyer" | "seller" | "observer">("observer");
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const listRef = React.useRef<HTMLDivElement>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        api.get(`/direct-chat/threads/${threadId}`),
        api.get(`/direct-chat/threads/${threadId}/messages`),
      ]);
      setThread(t.data?.thread);
      setViewerRole(t.data?.viewer_role || "observer");
      setMessages(m.data?.messages || []);
      // Mark as read (best-effort).
      await api.post(`/direct-chat/threads/${threadId}/read`).catch(() => {});
      onRead?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not load conversation.");
    } finally {
      setLoading(false);
    }
  }, [threadId, onRead]);

  React.useEffect(() => { load(); }, [load]);

  // Auto-scroll on new messages.
  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Poll for new messages every 8 s while the window is open.
  React.useEffect(() => {
    if (!threadId) return;
    let alive = true;
    const timer = window.setInterval(async () => {
      try {
        const r = await api.get(`/direct-chat/threads/${threadId}/messages`);
        if (!alive) return;
        setMessages(r.data?.messages || []);
      } catch { /* ignore */ }
    }, 8_000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [threadId]);

  async function send() {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    setDraft("");
    try {
      const r = await api.post(`/direct-chat/threads/${threadId}/messages`, { body });
      setMessages(prev => [...prev, r.data?.message]);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Message failed to send.");
      setDraft(body);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900 rounded-2xl border border-neutral-200 dark:border-white/10 overflow-hidden" data-testid="direct-chat-window">
      {/* Header */}
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-white/10 bg-neutral-50 dark:bg-slate-950/40 flex items-center gap-3">
        {thread?.product?.image_url && (
          <img src={thread.product.image_url} alt="" loading="lazy" decoding="async" className="h-10 w-10 rounded-lg object-cover border border-neutral-200 dark:border-white/10" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-neutral-900 dark:text-white truncate" data-testid="direct-chat-header-name">
            {viewerRole === "buyer"
              ? (thread?.seller_workspace_name || "Seller")
              : (thread?.buyer_name || "Buyer")}
          </div>
          <div className="text-[11px] text-neutral-500 dark:text-slate-400 truncate flex items-center gap-1">
            <Package className="h-3 w-3" />
            {thread?.product?.title || "General chat"}
          </div>
        </div>
        {thread?.closed && (
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-neutral-200 dark:bg-white/10 text-neutral-600 dark:text-slate-300">
            Closed
          </span>
        )}
      </div>

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="direct-chat-messages">
        {loading && (
          <div className="flex items-center justify-center h-full text-neutral-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-neutral-400 dark:text-slate-500 gap-2">
            <MessageCircle className="h-5 w-5" />
            <div className="text-[12px]">No messages yet — say hello.</div>
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map((m) => {
            const isMe = m.sender_user_id === user?.user_id;
            return (
              <motion.div
                key={m.message_id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                data-testid={`direct-chat-msg-${m.sender_role}`}
              >
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                  isMe
                    ? "bg-gradient-to-br from-orange-500 to-fuchsia-500 text-white rounded-br-md"
                    : "bg-neutral-100 dark:bg-white/10 text-neutral-800 dark:text-slate-100 rounded-bl-md"
                }`}>
                  {m.body}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="border-t border-neutral-200 dark:border-white/10 bg-neutral-50/60 dark:bg-slate-950/40 p-2 flex items-center gap-2"
      >
        <input
          data-testid="direct-chat-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={thread?.closed ? "This thread is closed" : "Type a message"}
          disabled={sending || thread?.closed || viewerRole === "observer"}
          className="flex-1 h-9 px-3 rounded-full text-sm bg-white dark:bg-white/10 border border-neutral-200 dark:border-white/10 text-neutral-900 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-slate-400 focus:outline-none focus:border-orange-400"
        />
        <Button
          type="submit"
          data-testid="direct-chat-send"
          disabled={sending || !draft.trim() || thread?.closed}
          size="sm"
          className="rounded-full h-9 w-9 p-0"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
};

export default DirectChatWindow;
